// d:\serverN8N\Webs\webCapturas2\src\components\AccountingEntryFormModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import useSupabaseClient from '../hooks/useSupabaseClient';
import Draggable from 'react-draggable';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { defaultAccountingDate, fiscalTaxType, validateManualRetention } from '../lib/manualAccountingEntry.mjs';

// Limitar las opciones para evitar lentitud al renderizar los dropdowns
const filterOptionsLimit = createFilterOptions({
  limit: 50,
  stringify: (option) => typeof option === 'string' ? option : `${option.codigo} ${option.descripcion}`
});

const AccountingEntryFormModal = ({ isOpen, onClose, entry, invoiceId, onSave, tenantConfig, erpListsStore, accountingEntries = [] }) => {
  const { supabase } = useSupabaseClient(); // We only need the client here for now
  const initialFormData = {
    invoice_id: invoiceId,
    entry_date: '', // Wait for the authoritative invoice date; never today's date.
    account_code: '',
    account_name: '',
    description: '',
    debit: 0,
    credit: 0,
    third_party_nit: '',
    third_party_name: '',
    cost_center_code: '',
    project_code: '',
    department_code: '',
    activity_code: '',
    document_reference: '',
    base_amount: 0,
    tax_rate: 0,
    observation: '',
    destino: 1,
    advance_cruce: '',
    advance_invc: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOptions, setAccountOptions] = useState([]);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const nodeRef = React.useRef(null);

  // Subscribe to ERP lists
  const [erpData, setErpData] = useState(erpListsStore ? erpListsStore.getState() : { costCenters: [], departments: [] });
  useEffect(() => {
    if (erpListsStore) {
      const unsub = erpListsStore.subscribe(setErpData);
      return unsub;
    }
  }, [erpListsStore]);

  // Optimización: Mapas para búsqueda en O(1)
  const costCentersMap = useMemo(() => {
    const map = new Map();
    (erpData.costCenters || []).forEach(c => map.set(c.codigo, c));
    return map;
  }, [erpData.costCenters]);

  const departmentsMap = useMemo(() => {
    const map = new Map();
    (erpData.departments || []).forEach(d => map.set(d.codigo, d));
    return map;
  }, [erpData.departments]);

  const searchAccounts = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    if (!t) return [];

    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    let user;
    try {
      user = JSON.parse(storedUser);
    } catch {
      return [];
    }
    if (!user?.id) return [];

    try {
      // Usar la función RPC para buscar simultáneamente por código (prefijo) o descripción
      const { data, error } = await supabase.rpc('search_accounts_by_prefix', {
        search_term: t,
        p_user_id: user.id
      });
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching accounts via RPC:", err);
      return [];
    }
  };

  const searchCostCenters = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_cost_centers').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching cost centers:", err);
      return [];
    }
  };

  const searchDepartments = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_departments').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching departments:", err);
      return [];
    }
  };

  const [costCenterSearch, setCostCenterSearch] = useState('');
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [isSearchingCostCenters, setIsSearchingCostCenters] = useState(false);
  const [ccAutocompleteOpen, setCcAutocompleteOpen] = useState(false);

  const [departmentSearch, setDepartmentSearch] = useState('');
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [isSearchingDepartments, setIsSearchingDepartments] = useState(false);
  const [deptAutocompleteOpen, setDeptAutocompleteOpen] = useState(false);

  const searchProjects = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_projects').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching projects:", err);
      return [];
    }
  };

  const searchActivities = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    const user = JSON.parse(storedUser);
    if (!user?.id) return [];

    try {
      let query = supabase.from('invoice_erp_activities').select('codigo, descripcion').eq('tenant_id', user.id).limit(50);
      if (t) {
        query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching activities:", err);
      return [];
    }
  };

  const [projectSearch, setProjectSearch] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  const [projAutocompleteOpen, setProjAutocompleteOpen] = useState(false);

  const [activitySearch, setActivitySearch] = useState('');
  const [activityOptions, setActivityOptions] = useState([]);
  const [isSearchingActivities, setIsSearchingActivities] = useState(false);
  const [actAutocompleteOpen, setActAutocompleteOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    
    // Si la búsqueda está vacía, no buscamos
    if (!accountSearch || typeof accountSearch !== 'string') {
      setAccountOptions([]);
      return;
    }

    setIsSearchingAccounts(true);
    const t = setTimeout(async () => {
      const opts = await searchAccounts(accountSearch);
      setAccountOptions(opts);
      setIsSearchingAccounts(false);
    }, 300);

    return () => clearTimeout(t);
  }, [accountSearch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setIsSearchingCostCenters(true);
    const t = setTimeout(async () => {
      const opts = await searchCostCenters(costCenterSearch);
      setCostCenterOptions(opts);
      setIsSearchingCostCenters(false);
    }, 300);
    return () => clearTimeout(t);
  }, [costCenterSearch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setIsSearchingDepartments(true);
    const t = setTimeout(async () => {
      const opts = await searchDepartments(departmentSearch);
      setDepartmentOptions(opts);
      setIsSearchingDepartments(false);
    }, 300);
    return () => clearTimeout(t);
  }, [departmentSearch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setIsSearchingProjects(true);
    const t = setTimeout(async () => {
      const opts = await searchProjects(projectSearch);
      setProjectOptions(opts);
      setIsSearchingProjects(false);
    }, 300);
    return () => clearTimeout(t);
  }, [projectSearch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setIsSearchingActivities(true);
    const t = setTimeout(async () => {
      const opts = await searchActivities(activitySearch);
      setActivityOptions(opts);
      setIsSearchingActivities(false);
    }, 300);
    return () => clearTimeout(t);
  }, [activitySearch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchInvoiceData = async (invId) => {
      if (!invId || !supabase) return;
      try {
        const storedUser = localStorage.getItem('invoice_user');
        if (!storedUser) return;
        const user = JSON.parse(storedUser);
        
        const { data, error } = await supabase
          .from('invoices')
          .select('num_identificacion, billing_name, subtotal, date')
          .eq('id', invId)
          .eq('user_id', user.id)
          .single();
          
        if (data && !error && !cancelled) {
          setInvoiceData(data);
          if (!entry) {
            setFormData(prev => ({
              ...prev,
              entry_date: prev.entry_date || defaultAccountingDate(accountingEntries, data.date),
              third_party_nit: prev.third_party_nit || data.num_identificacion || '',
              third_party_name: prev.third_party_name || data.billing_name || ''
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching invoice data:', err);
      }
    };

    if (entry) {
      setFormData({
        ...initialFormData,
        ...entry,
        debit: entry.debit || 0,
        credit: entry.credit || 0,
        base_amount: entry.base_amount || 0,
        tax_rate: entry.tax_rate || 0,
        destino: entry.destino ?? 1,
        entry_date: entry.entry_date ? new Date(entry.entry_date).toISOString().split('T')[0] : initialFormData.entry_date,
      });
      fetchInvoiceData(entry.invoice_id || invoiceId);
    } else {
      setFormData({...initialFormData, invoice_id: invoiceId});
      fetchInvoiceData(invoiceId);
    }
    return () => { cancelled = true; };
  }, [entry, invoiceId, isOpen]); // Re-initialize form when entry, invoiceId or isOpen changes

  // Function to fetch account description from database
  const fetchAccountDescription = async (accountCode) => {
    if (!accountCode || !supabase) return '';
    
    try {
      // Get current user from localStorage
      const storedUser = localStorage.getItem('invoice_user');
      if (!storedUser) return '';
      const user = JSON.parse(storedUser);
      
      const { data, error } = await supabase
        .from('invoice_chart_of_accounts')
        .select('description')
        .eq('account_code', parseInt(accountCode))
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching account description:', error);
        return '';
      }
      
      return data?.description || '';
    } catch (err) {
      console.error('Error in fetchAccountDescription:', err);
      return '';
    }
  };


  const fetchThirdPartyName = async (nit) => {
    if (!nit || !supabase) return '';
    try {
      const storedUser = localStorage.getItem('invoice_user');
      if (!storedUser) return '';
      const user = JSON.parse(storedUser);
      
      const { data, error } = await supabase
        .from('invoice_third_parties')
        .select('company, company_short')
        .eq('user_id', user.id)
        .or(`id_n.eq.${nit},nit.eq.${nit}`)
        .limit(1);
        
      if (error || !data || data.length === 0) return '';
      return data[0].company && data[0].company !== 'SIN NOMBRE' 
        ? data[0].company 
        : (data[0].company_short && data[0].company_short !== 'SIN NOMBRE' ? data[0].company_short : '');
    } catch (err) {
      console.error('Error fetching third party name:', err);
      return '';
    }
  };

  const handleChange = async (e) => {
    const { name, value, type } = e.target;
    if (name === 'third_party_nit') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
      
      const nitValue = value.trim();
      if (nitValue.length >= 3) {
        const fetchedName = await fetchThirdPartyName(nitValue);
        if (fetchedName) {
          setFormData(prev => ({
            ...prev,
            third_party_name: fetchedName
          }));
        }
      }
    } else if (name === 'base_amount' || name === 'tax_rate') {
      const numValue = type === 'number' ? parseFloat(value) || 0 : value;
      setFormData(prev => {
        const newState = { ...prev, [name]: numValue };
        if (newState.tax_rate > 0 && newState.base_amount > 0) {
          // Use typical rounding (2 decimal places max or integer depending on usage; since other parts use Math.round we'll keep it consistent, but actually standard accounting may use decimals if not COP. COP uses round. Let's stick to Math.round)
          const calculatedAmount = Math.round(newState.base_amount * (newState.tax_rate / 100));
          if (newState.debit > 0) {
            newState.debit = calculatedAmount;
          } else {
            newState.credit = calculatedAmount;
          }
        }
        return newState;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? parseFloat(value) || 0 : value,
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (formData.debit === 0 && formData.credit === 0) {
        setError('El débito o el crédito deben tener un valor.');
        setIsLoading(false);
        return;
    }
    if (!formData.account_code || !formData.account_name) {
        setError('El código y nombre de cuenta son obligatorios.');
        setIsLoading(false);
        return;
    }

    const retentionError = validateManualRetention(formData);
    if (!formData.entry_date || retentionError) {
      setError(retentionError || 'Seleccione la fecha del asiento; no se asigna la fecha de hoy automáticamente.');
      setIsLoading(false);
      return;
    }

    // Get current user from localStorage
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) {
      setError('Usuario no autenticado');
      setIsLoading(false);
      return;
    }
    const user = JSON.parse(storedUser);

    let result;
    const dataToSave = { ...formData };
    // Ensure numeric fields are numbers
    dataToSave.debit = Number(dataToSave.debit) || 0;
    dataToSave.credit = Number(dataToSave.credit) || 0;
    dataToSave.base_amount = Number(dataToSave.base_amount) || 0;
    dataToSave.tax_rate = Number(dataToSave.tax_rate) || 0;
    dataToSave.tax_type = fiscalTaxType(dataToSave.account_code) || dataToSave.tax_type || null;
    // Add user_id
    dataToSave.user_id = user.id;
    if (user.is_sub_user) {
      dataToSave.created_by_sub_user = user.sub_user_id;
    }


    if (entry && entry.id) {
      // Update existing entry
      const { data, error: updateError } = await supabase
        .from('accounting_entries')
        .update(dataToSave)
        .eq('id', entry.id)
        .eq('user_id', user.id)
        .select();
      result = { data, error: updateError };
    } else {
      // Create new entry
      // Remove id if it exists from initialFormData spread for new entries
      const { id: _id, ...newData } = dataToSave;
      const { data, error: insertError } = await supabase
        .from('accounting_entries')
        .insert(newData)
        .select();
      result = { data, error: insertError };
    }

    setIsLoading(false);

    if (result.error) {
      console.error('Error saving accounting entry:', result.error);
      setError(`Error al guardar: ${result.error.message}`);
    } else {
      // --- CAPTURA DE CORRECCIONES PARA INTELIGENCIA ARTIFICIAL (ML) ---
      // Si la cuenta empieza por 1, 5, 6, o 7 es útil para la clasificación semántica de ML.
      try {
        const firstDigit = String(formData.account_code).charAt(0);
        if (['1', '5', '6', '7'].includes(firstDigit)) {
          const mlPayload = {
             tenant_user_id: user.id,
             invoice_id: formData.invoice_id || null,
             supplier_nit: formData.third_party_nit || null,
             item_description: formData.description || 'Corrección o ingreso manual en asiento',
             account_code: formData.account_code,
             account_scope: 'tenant',
             was_user_corrected: true, // Esto previene que el Garbage Collector de SAIDB lo borre
             document_date: formData.entry_date || null
          };
          
          if (tenantConfig?.enable_destino_field) {
             mlPayload.destino = formData.destino;
          }

          // Vincular el ID del asiento original si está disponible
          if (result.data && result.data[0] && result.data[0].id) {
             mlPayload.source_accounting_entry_id = result.data[0].id;
          } else if (entry && entry.id) {
             mlPayload.source_accounting_entry_id = entry.id;
          }

          // Insertar en la tabla de entrenamiento sin bloquear el hilo principal ni mostrar errores
          supabase.from('invoice_ml_training_examples')
            .insert(mlPayload)
            .then(({ error: mlError }) => {
               if (mlError) console.error('⚠️ No se pudo guardar la corrección ML:', mlError.message);
               else console.log('✅ Corrección inyectada con éxito al dataset de entrenamiento ML.');
            });
        }
      } catch (mlSysErr) {
        console.error('Excepción al inyectar corrección ML:', mlSysErr);
      }
      
      onSave(result.data[0]); // Pass the saved/updated entry back
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setError('');
    setAccountSearch('');
    setAccountOptions([]);
    onClose();
  };

  // Memoize el valor seleccionado para evitar que Autocomplete resetee el texto al tipear
  const selectedAccountValue = React.useMemo(() => {
    return accountOptions.find(o => String(o.account_code) === String(formData.account_code)) || 
           (formData.account_code ? { account_code: formData.account_code, description: formData.account_name } : null);
  }, [formData.account_code, formData.account_name, accountOptions]);

  if (!isOpen) return null;

  // Basic styling - you'll want to improve this with your project's styles
  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const contentStyle = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '600px',
    maxWidth: '90%',
    maxHeight: '90%',
    overflowY: 'auto',
  };

  const labelStyle = { display: 'block', margin: '10px 0 5px' };
  const inputStyle = { width: '100%', padding: '8px', boxSizing: 'border-box' };
  const buttonStyle = { padding: '10px 15px', margin: '10px 5px 0', border: 'none', borderRadius: '4px', cursor: 'pointer' };

  const titleBarStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '10px',
    cursor: 'move',
    userSelect: 'none',
  };

  const closeButtonStyle = {
    background: 'transparent',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
  };

  return (
    <div style={modalStyle}>
      <Draggable nodeRef={nodeRef} handle={'.draggable-dialog-title'} cancel={'input,textarea,select,button'}>
        <div ref={nodeRef} style={contentStyle} onClick={(e) => e.stopPropagation()}>
          <div className="draggable-dialog-title" style={titleBarStyle}>
            <h2 style={{ margin: 0 }}>{entry ? 'Editar Asiento Contable' : 'Agregar Asiento Contable'}</h2>
            <button type="button" onClick={handleClose} disabled={isLoading} style={closeButtonStyle} aria-label="Cerrar">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <label style={labelStyle} htmlFor="entry_date">Fecha Asiento:</label>
            <input style={inputStyle} type="date" id="entry_date" name="entry_date" value={formData.entry_date} onChange={handleChange} required aria-describedby="entry-date-help" />
            <p id="entry-date-help" style={{ fontSize: '0.8rem', marginTop: '-0.5rem' }}>Al enviar a Firebird, todos los movimientos usarán la fecha de emisión de la factura.</p>

            <label style={labelStyle} htmlFor="account_code">Código Cuenta:</label>
            <Autocomplete
              open={autocompleteOpen}
              onOpen={() => setAutocompleteOpen(true)}
              onClose={() => setAutocompleteOpen(false)}
              value={selectedAccountValue}
              onChange={async (event, newValue) => {
                if (newValue) {
                  const accountCodeStr = String(newValue.account_code);
                  const accountNameStr = newValue.description || '';
                  
                  // Set immediately for UI responsiveness
                  setFormData(prev => ({
                    ...prev,
                    account_code: accountCodeStr,
                    account_name: accountNameStr
                  }));

                  // Check if account has a withholding percentage and is a retention account
                  try {
                    const storedUser = localStorage.getItem('invoice_user');
                    if (storedUser && supabase) {
                      const user = JSON.parse(storedUser);
                      const { data: accData, error } = await supabase
                        .from('invoice_chart_of_accounts')
                        .select('withholding_percentage, tax_application, account_code')
                        .eq('account_code', parseInt(newValue.account_code))
                        .eq('user_id', user.id)
                        .single();

                      if (!error && accData && accData.withholding_percentage > 0 && invoiceData) {
                        // Check if it's actually a retention account
                        const isRetention = ['RETEFUENTE', 'ICA', 'RETEIVA'].includes(accData.tax_application) || 
                                            (accData.account_code && accData.account_code.toString().startsWith('236')) ||
                                            (accData.account_code && accData.account_code.toString().startsWith('1355'));
                                            
                        if (isRetention) {
                          const subtotal = parseFloat(invoiceData.subtotal) || 0;
                          const taxRate = parseFloat(accData.withholding_percentage);
                          const calculatedValue = (subtotal * taxRate) / 100;
                          
                          setFormData(prev => {
                             // Only apply if the account code hasn't changed since the request started
                             if (prev.account_code === accountCodeStr) {
                               return {
                                 ...prev,
                                 base_amount: subtotal,
                                 tax_rate: taxRate,
                                 credit: Math.round(calculatedValue * 100) / 100,
                                 debit: 0
                               };
                             }
                             return prev;
                          });
                        }
                      }
                    }
                  } catch (err) {
                    console.error('Error fetching withholding percentage:', err);
                  }
                } else {
                  setFormData(prev => ({
                    ...prev,
                    account_code: '',
                    account_name: ''
                  }));
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setAccountSearch(newInputValue);
                  // Limpiamos la selección si el usuario está tipeando una nueva búsqueda
                  if (formData.account_code) {
                    setFormData(prev => ({ ...prev, account_code: '', account_name: '' }));
                  }
                } else if (reason === 'clear') {
                  setAccountSearch('');
                  setFormData(prev => ({ ...prev, account_code: '', account_name: '' }));
                }
              }}
              options={accountOptions}
              getOptionLabel={(option) => `${option.account_code || ''} - ${option.description || ''}`}
              isOptionEqualToValue={(option, value) => String(option.account_code) === String(value.account_code)}
              loading={isSearchingAccounts}
              loadingText="Buscando..."
              noOptionsText={accountSearch ? "No se encontraron cuentas" : "Escribe para buscar"}
              filterOptions={(x) => x} // El filtrado se hace en el servidor vía Supabase
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  placeholder="Escribe código o parte de la descripción"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isSearchingAccounts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  sx={{ 
                    backgroundColor: 'white', 
                    '& .MuiOutlinedInput-root': { padding: '4px' } 
                  }}
                />
              )}
            />

            <label style={labelStyle} htmlFor="account_name">Nombre Cuenta:</label>
            <input style={inputStyle} type="text" id="account_name" name="account_name" value={formData.account_name} readOnly required />

            <label style={labelStyle} htmlFor="description">Descripción:</label>
            <textarea style={{ ...inputStyle, height: '60px' }} id="description" name="description" value={formData.description} onChange={handleChange}></textarea>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ width: '48%' }}>
                <label style={labelStyle} htmlFor="debit">Débito:</label>
                <input style={inputStyle} type="number" step="0.01" id="debit" name="debit" value={formData.debit} onChange={handleChange} />
              </div>
              <div style={{ width: '48%' }}>
                <label style={labelStyle} htmlFor="credit">Crédito:</label>
                <input style={inputStyle} type="number" step="0.01" id="credit" name="credit" value={formData.credit} onChange={handleChange} />
              </div>
            </div>

            {/* Impuestos / Retenciones */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ width: '48%' }}>
                <label style={labelStyle} htmlFor="base_amount">Valor Base (Impuestos):</label>
                <input style={inputStyle} type="number" step="0.01" id="base_amount" name="base_amount" value={formData.base_amount} onChange={handleChange} />
              </div>
              <div style={{ width: '48%' }}>
                <label style={labelStyle} htmlFor="tax_rate">Tasa Impuesto (%):</label>
                <input style={inputStyle} type="number" step="0.01" id="tax_rate" name="tax_rate" value={formData.tax_rate} onChange={handleChange} />
              </div>
            </div>

            {/* Proveedores (Terceros) */}
            <label style={labelStyle} htmlFor="third_party_nit">NIT Tercero:</label>
            <input style={inputStyle} type="text" id="third_party_nit" name="third_party_nit" value={formData.third_party_nit} onChange={handleChange} />

            <label style={labelStyle} htmlFor="third_party_name">Nombre Tercero:</label>
            <input style={inputStyle} type="text" id="third_party_name" name="third_party_name" value={formData.third_party_name} onChange={handleChange} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ flex: '1 1 45%' }}>
                <label style={labelStyle} htmlFor="cost_center_code">Centro de Costo:</label>
                <Autocomplete
                  size="small"
                  open={ccAutocompleteOpen}
                  onOpen={() => setCcAutocompleteOpen(true)}
                  onClose={() => setCcAutocompleteOpen(false)}
                  isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} - ${option.descripcion}`}
                  options={costCenterOptions}
                  loading={isSearchingCostCenters}
                  inputValue={costCenterSearch}
                  onInputChange={(event, newInputValue, reason) => {
                    setCostCenterSearch(newInputValue);
                    if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, cost_center_code: '' }));
                    }
                  }}
                  value={costCenterOptions.find(o => o.codigo === formData.cost_center_code) || (formData.cost_center_code ? { codigo: formData.cost_center_code, descripcion: '' } : null)}
                  onChange={(event, newValue) => {
                    const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : '';
                    setFormData(prev => ({ ...prev, cost_center_code: newCode }));
                    if (newValue && typeof newValue !== 'string') {
                      setCostCenterSearch(`${newValue.codigo} - ${newValue.descripcion}`);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar por código o descripción..."
                      sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-root': { padding: '4px' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearchingCostCenters ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </div>
              <div style={{ flex: '1 1 45%' }}>
                <label style={labelStyle} htmlFor="project_code">Proyecto:</label>
                <Autocomplete
                  size="small"
                  open={projAutocompleteOpen}
                  onOpen={() => setProjAutocompleteOpen(true)}
                  onClose={() => setProjAutocompleteOpen(false)}
                  isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} - ${option.descripcion}`}
                  options={projectOptions}
                  loading={isSearchingProjects}
                  inputValue={projectSearch}
                  onInputChange={(event, newInputValue, reason) => {
                    setProjectSearch(newInputValue);
                    if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, project_code: '' }));
                    }
                  }}
                  value={projectOptions.find(o => o.codigo === formData.project_code) || (formData.project_code ? { codigo: formData.project_code, descripcion: '' } : null)}
                  onChange={(event, newValue) => {
                    const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : '';
                    setFormData(prev => ({ ...prev, project_code: newCode }));
                    if (newValue && typeof newValue !== 'string') {
                      setProjectSearch(`${newValue.codigo} - ${newValue.descripcion}`);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar por código o descripción..."
                      sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-root': { padding: '4px' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearchingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </div>
              <div style={{ flex: '1 1 45%' }}>
                <label style={labelStyle} htmlFor="department_code">Departamento:</label>
                <Autocomplete
                  size="small"
                  open={deptAutocompleteOpen}
                  onOpen={() => setDeptAutocompleteOpen(true)}
                  onClose={() => setDeptAutocompleteOpen(false)}
                  isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} - ${option.descripcion}`}
                  options={departmentOptions}
                  loading={isSearchingDepartments}
                  inputValue={departmentSearch}
                  onInputChange={(event, newInputValue, reason) => {
                    setDepartmentSearch(newInputValue);
                    if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, department_code: '' }));
                    }
                  }}
                  value={departmentOptions.find(o => o.codigo === formData.department_code) || (formData.department_code ? { codigo: formData.department_code, descripcion: '' } : null)}
                  onChange={(event, newValue) => {
                    const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : '';
                    setFormData(prev => ({ ...prev, department_code: newCode }));
                    if (newValue && typeof newValue !== 'string') {
                      setDepartmentSearch(`${newValue.codigo} - ${newValue.descripcion}`);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar por código o descripción..."
                      sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-root': { padding: '4px' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearchingDepartments ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </div>
              <div style={{ flex: '1 1 45%' }}>
                <label style={labelStyle} htmlFor="activity_code">Actividad:</label>
                <Autocomplete
                  size="small"
                  open={actAutocompleteOpen}
                  onOpen={() => setActAutocompleteOpen(true)}
                  onClose={() => setActAutocompleteOpen(false)}
                  isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} - ${option.descripcion}`}
                  options={activityOptions}
                  loading={isSearchingActivities}
                  inputValue={activitySearch}
                  onInputChange={(event, newInputValue, reason) => {
                    setActivitySearch(newInputValue);
                    if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, activity_code: '' }));
                    }
                  }}
                  value={activityOptions.find(o => o.codigo === formData.activity_code) || (formData.activity_code ? { codigo: formData.activity_code, descripcion: '' } : null)}
                  onChange={(event, newValue) => {
                    const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : '';
                    setFormData(prev => ({ ...prev, activity_code: newCode }));
                    if (newValue && typeof newValue !== 'string') {
                      setActivitySearch(`${newValue.codigo} - ${newValue.descripcion}`);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar por código o descripción..."
                      sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-root': { padding: '4px' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearchingActivities ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </div>
              {tenantConfig?.enable_destino_field && (String(formData.account_code).startsWith('5') || String(formData.account_code).startsWith('7')) && (
                <div style={{ flex: '1 1 45%' }}>
                  <label style={labelStyle} htmlFor="destino">Destino:</label>
                  <input style={inputStyle} type="number" id="destino" name="destino" value={formData.destino} onChange={handleChange} />
                </div>
              )}
            </div>

            {tenantConfig?.enable_ea_sync && String(formData.account_code) === String(tenantConfig?.ea_bridge_account) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                <div style={{ width: '48%' }}>
                  <label style={labelStyle} htmlFor="advance_cruce">Tipo Doc (Cruce EA):</label>
                  <input style={inputStyle} type="text" id="advance_cruce" name="advance_cruce" value={formData.advance_cruce || ''} onChange={handleChange} />
                </div>
                <div style={{ width: '48%' }}>
                  <label style={labelStyle} htmlFor="advance_invc">Número Doc (Cruce EA):</label>
                  <input style={inputStyle} type="text" id="advance_invc" name="advance_invc" value={formData.advance_invc || ''} onChange={handleChange} />
                </div>
              </div>
            )}

            <label style={labelStyle} htmlFor="document_reference">Documento Referencia:</label>
            <input style={inputStyle} type="text" id="document_reference" name="document_reference" value={formData.document_reference} onChange={handleChange} />

            <label style={labelStyle} htmlFor="observation">Observaciones:</label>
            <textarea style={{ ...inputStyle, height: '60px' }} id="observation" name="observation" value={formData.observation} onChange={handleChange}></textarea>

            {error && <p style={{ color: 'red' }}>{error}</p>}

            <div style={{ textAlign: 'right' }}>
              <button style={{ ...buttonStyle, backgroundColor: '#ccc' }} type="button" onClick={handleClose} disabled={isLoading}>Cancelar</button>
              <button style={{ ...buttonStyle, backgroundColor: '#007bff', color: 'white' }} type="submit" disabled={isLoading}>
                {isLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </Draggable>
    </div>
  );
};

export default AccountingEntryFormModal;
